import { CHANGE_PASSWORD, COMPANY_PROFILE, LOGIN, USER_PROFILE } from "@constants/internal-route.constant";
import { useAuth } from "@context/Auth";
import { useBranding } from "@context/Branding";
import Dropdown from "react-bootstrap/Dropdown";
import { Link, useNavigate } from "react-router-dom";
import { readAccess } from "routes/access";
import { initials, roleKeyLabel } from "utility/people";
import { ColorMode, COLOR_MODE_KEY } from "./useColorMode";

const MODES: { mode: ColorMode; icon: string; label: string }[] = [
  { mode: "light", icon: "bi-sun", label: "Light" },
  { mode: "dark", icon: "bi-moon-stars", label: "Dark" },
  { mode: "auto", icon: "bi-circle-half", label: "Auto" },
];

// Browser preferences that must survive the localStorage.clear() on logout.
const KEEP_ON_LOGOUT = [COLOR_MODE_KEY];

type Props = { mode: ColorMode; onMode: (m: ColorMode) => void };

const UserMenu = ({ mode, onMode }: Props) => {
  const navigate = useNavigate();
  const { logout, currentUser } = useAuth();
  const { companyName } = useBranding();
  const user: any = currentUser;
  const name = user?.fullName || user?.name || user?.email || "Account";
  const isSuperAdmin = readAccess().isSuperAdmin;

  // Await the logout before clearing storage: the request picks the token up from storage in a
  // later microtask, so clearing first sends it without one and the server never revokes it.
  const handleLogout = async () => {
    await logout();
    const kept = KEEP_ON_LOGOUT.map((k) => [k, localStorage.getItem(k)] as const);
    localStorage.clear();
    kept.forEach(([k, v]) => v !== null && localStorage.setItem(k, v));
    navigate(LOGIN);
  };

  return (
    <Dropdown as="li" className="nav-item user-menu" align="end">
      <Dropdown.Toggle as="button" bsPrefix="erp-header-btn" className="nav-link" aria-label="Account menu">
        <span className="erp-avatar">{initials(name)}</span>
        <span className="d-none d-md-inline">{name}</span>
        <i className="bi bi-chevron-down small d-none d-md-inline" />
      </Dropdown.Toggle>

      <Dropdown.Menu as="ul" className="dropdown-menu-lg">
        <li className="user-header text-bg-primary">
          <span className="erp-avatar erp-avatar-lg mb-2">{initials(name)}</span>
          <p className="mb-0">
            {name}
            <small className="d-block">
              {roleKeyLabel(user?.roleKey || user?.roleName)}
              {companyName ? ` · ${companyName}` : ""}
            </small>
          </p>
        </li>
        <li>
          <Dropdown.Item as={Link} to={USER_PROFILE}>
            <i className="bi bi-person me-2" />
            My profile
          </Dropdown.Item>
        </li>
        {isSuperAdmin && (
          <li>
            <Dropdown.Item as={Link} to={COMPANY_PROFILE}>
              <i className="bi bi-building me-2" />
              Company profile
            </Dropdown.Item>
          </li>
        )}
        <li>
          <Dropdown.Item as={Link} to={CHANGE_PASSWORD}>
            <i className="bi bi-key me-2" />
            Change password
          </Dropdown.Item>
        </li>
        <li>
          <Dropdown.Divider />
        </li>
        <li className="px-3 py-1">
          <div className="small text-body-secondary mb-1">Appearance</div>
          <div className="btn-group btn-group-sm w-100 erp-theme-switch" role="group" aria-label="Colour mode">
            {MODES.map((m) => (
              <button
                key={m.mode}
                type="button"
                aria-pressed={mode === m.mode}
                className={`btn ${mode === m.mode ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => onMode(m.mode)}
              >
                <i className={`bi ${m.icon} me-1`} />
                {m.label}
              </button>
            ))}
          </div>
        </li>
        <li>
          <Dropdown.Divider />
        </li>
        <li>
          <Dropdown.Item as="button" className="text-danger" onClick={handleLogout}>
            <i className="bi bi-box-arrow-right me-2" />
            Sign out
          </Dropdown.Item>
        </li>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default UserMenu;
